import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchParkGeofenceDetails,
  deleteParkGeofence,
} from "../redux/actions/parkGeofenceActions";
import Papa from "papaparse";
import {
  FiSearch,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { GoogleMap, Polygon, useJsApiLoader } from "@react-google-maps/api";
import theme from "../components/shared/theme";
import {
  dropdownStyle,
  thStyle,
  tdStyle,
  paginationButtonStyle,
} from "../components/shared/themeStyles";

const containerStyle = { width: "100%", height: "400px" };

const ParkGeofence = () => {
  const dispatch = useDispatch();
  const { geofenceList, loading, error } = useSelector(
    (state) => state.parkGeofence
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipalityFilter, setSelectedMunicipalityFilter] =
    useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const itemsPerPage = 10;

  // Google Maps loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
  });

  useEffect(() => {
    dispatch(fetchParkGeofenceDetails());
  }, [dispatch]);

  const districts = [
    ...new Set(geofenceList.map((g) => g.district_name).filter(Boolean)),
  ];
  const municipalities = [
    ...new Set(geofenceList.map((g) => g.municipality_name).filter(Boolean)),
  ];

  // Filter
  const filteredList = geofenceList.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      item.park_name?.toLowerCase().includes(searchLower) ||
      item.district_name?.toLowerCase().includes(searchLower) ||
      item.municipality_name?.toLowerCase().includes(searchLower);

    const matchesDistrict = selectedDistrict
      ? item.district_name === selectedDistrict
      : true;
    const matchesMunicipality = selectedMunicipalityFilter
      ? item.municipality_name === selectedMunicipalityFilter
      : true;

    return matchesSearch && matchesDistrict && matchesMunicipality;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);

  // CSV Export
  const handleCSVDownload = () => {
    const rows = filteredList.map((item) => ({
      District: item.district_name,
      Municipality: item.municipality_name,
      Park: item.park_name,
      Description: item.description,
      Latitude: item.latitude,
      Longitude: item.longitude,
      Radius: item.radius_meters,
      GeofenceType: item.geofence_type,
      Status: item.status,
      CreatedAt: item.created_at
        ? new Date(item.created_at).toLocaleString("en-IN")
        : "-",
      UpdatedAt: item.updated_at
        ? new Date(item.updated_at).toLocaleString("en-IN")
        : "-",
    }));

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "park_geofence_list.csv";
    link.click();
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this park geofence?")) {
      dispatch(deleteParkGeofence(id));
    }
  };

  // Convert POLYGON string to LatLng array
  const getPolygonPath = (polygonStr) => {
    if (!polygonStr) return [];
    return polygonStr
      .replace("POLYGON((", "")
      .replace("))", "")
      .split(",")
      .map((coord) => {
        const [lng, lat] = coord.trim().split(" ");
        return { lat: parseFloat(lat), lng: parseFloat(lng) };
      });
  };

  return (
    <div className="w-full min-h-screen bg-gray-100 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Park Geofence</h1>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap gap-3 items-center bg-white p-4 rounded-lg shadow mb-6">
        <select
          value={selectedDistrict}
          onChange={(e) => {
            setSelectedDistrict(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={selectedMunicipalityFilter}
          onChange={(e) => {
            setSelectedMunicipalityFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {municipalities.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <div className="relative w-full sm:w-[260px]">
          <FiSearch className="absolute left-3 top-3 text-gray-500" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...dropdownStyle, paddingLeft: theme.spacing.xl }}
          />
        </div>

        <button
          onClick={handleCSVDownload}
          style={{
            backgroundColor: theme.colors.secondary,
            color: "#fff",
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: theme.radius.md,
            fontWeight: theme.font.weight.medium,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            height: "42px",
            marginLeft: "auto",
          }}
        >
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-gray-700">Loading park geofences...</p>
      ) : error ? (
        <p className="text-red-600">Error: {error}</p>
      ) : currentItems.length > 0 ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-[900px] text-sm text-left">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Park</th>
                <th style={thStyle}>District</th>
                <th style={thStyle}>Municipality</th>
                <th style={thStyle}>Latitude</th>
                <th style={thStyle}>Longitude</th>
                <th style={thStyle}>Radius (m)</th>
                <th style={thStyle}>Geofence Type</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.map((item, index) => (
                <tr key={item.id}>
                  <td style={tdStyle}>{indexOfFirstItem + index + 1}</td>
                  <td style={tdStyle}>{item.park_name}</td>
                  <td style={tdStyle}>{item.district_name}</td>
                  <td style={tdStyle}>{item.municipality_name}</td>
                  <td style={tdStyle}>{item.latitude}</td>
                  <td style={tdStyle}>{item.longitude}</td>
                  <td style={tdStyle}>{item.radius_meters}</td>
                  <td style={tdStyle}>{item.geofence_type}</td>
                  <td style={tdStyle}>{item.status}</td>
                  <td style={tdStyle} className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedGeofence(item);
                        setIsModalOpen(true);
                      }}
                      style={{
                        backgroundColor: theme.colors.primary,
                        color: "white",
                        borderRadius: theme.radius.sm,
                        padding: "6px 12px",
                        fontSize: theme.font.size.sm,
                        minWidth: "100px",
                        textAlign: "center",
                      }}
                    >
                      View Polygon
                    </button>

                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{
                        backgroundColor: theme.colors.error,
                        color: "Red",
                        borderRadius: theme.radius.sm,
                        padding: "6px 12px",
                        fontSize: theme.font.size.sm,
                        minWidth: "80px",
                        textAlign: "center",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex justify-between items-center p-4 border-t bg-gray-50">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={paginationButtonStyle}
            >
              <FiChevronLeft /> Previous
            </button>
            <p className="text-sm">
              Page {currentPage} of {totalPages}
            </p>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              style={paginationButtonStyle}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      ) : (
        <p className="text-gray-600 mt-4">No park geofences found.</p>
      )}

      {/* Geofence Polygon Modal */}
      {isModalOpen && selectedGeofence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[90%] max-w-2xl p-4 relative">
            <h2 className="text-xl font-bold mb-4">
              {selectedGeofence.park_name}
            </h2>
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-2 right-2 text-red-600 font-bold text-lg"
            >
              ✕
            </button>
            {isLoaded && selectedGeofence.geofence_polygon ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={getPolygonPath(selectedGeofence.geofence_polygon)[0]}
                zoom={18}
              >
                <Polygon
                  paths={getPolygonPath(selectedGeofence.geofence_polygon)}
                  options={{
                    fillColor: "#FF0000",
                    fillOpacity: 0.35,
                    strokeColor: "#FF0000",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              </GoogleMap>
            ) : (
              <p className="text-gray-700">Loading map...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkGeofence;
